import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const SLUG_RE = /^[a-z0-9-]+$/;

const PLATFORMS = [
  'instagram', 'tiktok', 'youtube', 'spotify',
  'apple_music', 'facebook', 'x', 'website', 'other',
];

const DEFAULT_FORM = {
  display_name: '',
  slug: '',
  professional_category: '',
  short_bio: '',
  full_bio: '',
  country: '',
  genres: '',
  profile_image_url: '',
  cover_image_url: '',
  website_url: '',
  status: 'draft',
  is_featured: false,
  display_order: '',
  seo_title: '',
  seo_description: '',
  social_links: [],
};

function slugify(str) {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

function getToken() {
  return sessionStorage.getItem('wp_token');
}

export default function AdminRosterEdit() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();

  const [form, setForm] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (isNew) return;
    fetch(`/api/publishing/admin/roster/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        if (!res.ok) throw new Error(`Failed to load profile (${res.status})`);
        return res.json();
      })
      .then(data => {
        setForm({
          display_name: data.display_name || '',
          slug: data.slug || '',
          professional_category: data.professional_category || '',
          short_bio: data.short_bio || '',
          full_bio: data.full_bio || '',
          country: data.country || '',
          genres: Array.isArray(data.genres)
            ? data.genres.join(', ')
            : data.genres || '',
          profile_image_url: data.profile_image_url || '',
          cover_image_url: data.cover_image_url || '',
          website_url: data.website_url || '',
          status: data.status || 'draft',
          is_featured: Boolean(data.is_featured),
          display_order: data.display_order ?? '',
          seo_title: data.seo_title || '',
          seo_description: data.seo_description || '',
          social_links: Array.isArray(data.social_links) ? data.social_links : [],
        });
        setLoading(false);
      })
      .catch(err => {
        setFetchError(err.message);
        setLoading(false);
      });
  }, [id, isNew]);

  const setField = useCallback((key, value) => {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'display_name' && isNew && !slugTouched) {
        next.slug = slugify(value);
      }
      return next;
    });
    setErrors(prev => ({ ...prev, [key]: undefined }));
  }, [isNew, slugTouched]);

  const validate = () => {
    const errs = {};
    if (!form.display_name.trim()) {
      errs.display_name = 'Display name is required.';
    }
    if (!form.slug.trim()) {
      errs.slug = 'Slug is required.';
    } else if (!SLUG_RE.test(form.slug)) {
      errs.slug = 'Slug may only contain lowercase letters, numbers, and hyphens.';
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setSaving(true);
    setErrors({});

    const payload = {
      ...form,
      genres: form.genres
        ? form.genres.split(',').map(g => g.trim()).filter(Boolean)
        : [],
      display_order: form.display_order === '' ? null : Number(form.display_order),
    };

    try {
      const url = isNew
        ? '/api/publishing/admin/roster'
        : `/api/publishing/admin/roster/${id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Request failed (${res.status})`);
      }
      navigate('/app/portal/admin/roster');
    } catch (err) {
      setErrors({ _form: err.message });
      setSaving(false);
    }
  };

  const addSocialLink = () => {
    setForm(prev => ({
      ...prev,
      social_links: [...prev.social_links, { platform: 'instagram', url: '' }],
    }));
  };

  const updateSocialLink = (idx, key, value) => {
    setForm(prev => {
      const links = [...prev.social_links];
      links[idx] = { ...links[idx], [key]: value };
      return { ...prev, social_links: links };
    });
  };

  const removeSocialLink = (idx) => {
    setForm(prev => ({
      ...prev,
      social_links: prev.social_links.filter((_, i) => i !== idx),
    }));
  };

  if (loading) {
    return (
      <>
        <h1 className="wp-portal__page-title">Edit Profile</h1>
        <p className="wp-portal__page-sub">Loading...</p>
      </>
    );
  }

  if (fetchError) {
    return (
      <>
        <h1 className="wp-portal__page-title">Edit Profile</h1>
        <div className="wp-portal__error">{fetchError}</div>
      </>
    );
  }

  return (
    <>
      <div className="wp-portal__page-header">
        <div>
          <h1 className="wp-portal__page-title">
            {isNew ? 'New Profile' : 'Edit Profile'}
          </h1>
          <p className="wp-portal__page-sub">
            {isNew ? 'Create a new roster profile.' : `Editing profile ID: ${id}`}
          </p>
        </div>
      </div>

      <form className="wp-portal__form" onSubmit={handleSubmit} noValidate>
        {errors._form && (
          <div className="wp-portal__error wp-portal__error--form">{errors._form}</div>
        )}

        <div className="wp-portal__form-section">
          <h2 className="wp-portal__form-section-title">Basic Information</h2>

          <div className="wp-portal__field">
            <label className="wp-portal__label" htmlFor="display_name">
              Display Name <span className="wp-portal__required">*</span>
            </label>
            <input
              id="display_name"
              className={`wp-portal__input${errors.display_name ? ' wp-portal__input--error' : ''}`}
              type="text"
              value={form.display_name}
              onChange={e => setField('display_name', e.target.value)}
            />
            {errors.display_name && (
              <p className="wp-portal__field-error">{errors.display_name}</p>
            )}
          </div>

          <div className="wp-portal__field">
            <label className="wp-portal__label" htmlFor="slug">
              Slug <span className="wp-portal__required">*</span>
            </label>
            <input
              id="slug"
              className={`wp-portal__input${errors.slug ? ' wp-portal__input--error' : ''}`}
              type="text"
              value={form.slug}
              onChange={e => {
                setSlugTouched(true);
                setField('slug', e.target.value);
              }}
            />
            {errors.slug && (
              <p className="wp-portal__field-error">{errors.slug}</p>
            )}
          </div>

          <div className="wp-portal__field">
            <label className="wp-portal__label" htmlFor="professional_category">
              Professional Category
            </label>
            <input
              id="professional_category"
              className="wp-portal__input"
              type="text"
              value={form.professional_category}
              onChange={e => setField('professional_category', e.target.value)}
            />
          </div>

          <div className="wp-portal__field">
            <label className="wp-portal__label" htmlFor="country">Country</label>
            <input
              id="country"
              className="wp-portal__input"
              type="text"
              value={form.country}
              onChange={e => setField('country', e.target.value)}
            />
          </div>

          <div className="wp-portal__field">
            <label className="wp-portal__label" htmlFor="genres">
              Genres{' '}
              <span className="wp-portal__hint">(comma-separated)</span>
            </label>
            <input
              id="genres"
              className="wp-portal__input"
              type="text"
              value={form.genres}
              onChange={e => setField('genres', e.target.value)}
              placeholder="e.g. R&B, Soul, Pop"
            />
          </div>
        </div>

        <div className="wp-portal__form-section">
          <h2 className="wp-portal__form-section-title">Bio</h2>

          <div className="wp-portal__field">
            <label className="wp-portal__label" htmlFor="short_bio">Short Bio</label>
            <textarea
              id="short_bio"
              className="wp-portal__input wp-portal__textarea"
              rows={3}
              value={form.short_bio}
              onChange={e => setField('short_bio', e.target.value)}
            />
          </div>

          <div className="wp-portal__field">
            <label className="wp-portal__label" htmlFor="full_bio">Full Bio</label>
            <textarea
              id="full_bio"
              className="wp-portal__input wp-portal__textarea"
              rows={6}
              value={form.full_bio}
              onChange={e => setField('full_bio', e.target.value)}
            />
          </div>
        </div>

        <div className="wp-portal__form-section">
          <h2 className="wp-portal__form-section-title">Media &amp; Links</h2>

          <div className="wp-portal__field">
            <label className="wp-portal__label" htmlFor="profile_image_url">
              Profile Image URL
            </label>
            <input
              id="profile_image_url"
              className="wp-portal__input"
              type="url"
              value={form.profile_image_url}
              onChange={e => setField('profile_image_url', e.target.value)}
            />
          </div>

          <div className="wp-portal__field">
            <label className="wp-portal__label" htmlFor="cover_image_url">
              Cover Image URL
            </label>
            <input
              id="cover_image_url"
              className="wp-portal__input"
              type="url"
              value={form.cover_image_url}
              onChange={e => setField('cover_image_url', e.target.value)}
            />
          </div>

          <div className="wp-portal__field">
            <label className="wp-portal__label" htmlFor="website_url">
              Website URL
            </label>
            <input
              id="website_url"
              className="wp-portal__input"
              type="url"
              value={form.website_url}
              onChange={e => setField('website_url', e.target.value)}
            />
          </div>
        </div>

        <div className="wp-portal__form-section">
          <h2 className="wp-portal__form-section-title">Social Links</h2>

          {form.social_links.length === 0 && (
            <p className="wp-portal__page-sub">No social links added.</p>
          )}

          {form.social_links.map((link, idx) => (
            <div key={idx} className="wp-portal__social-row">
              <select
                className="wp-portal__input wp-portal__select wp-portal__social-platform"
                value={link.platform}
                onChange={e => updateSocialLink(idx, 'platform', e.target.value)}
              >
                {PLATFORMS.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <input
                className="wp-portal__input wp-portal__social-url"
                type="url"
                placeholder="https://..."
                value={link.url}
                onChange={e => updateSocialLink(idx, 'url', e.target.value)}
              />
              <button
                type="button"
                className="wp-portal__btn wp-portal__btn--sm wp-portal__btn--danger"
                onClick={() => removeSocialLink(idx)}
              >
                Remove
              </button>
            </div>
          ))}

          <button
            type="button"
            className="wp-portal__btn wp-portal__btn--ghost wp-portal__btn--sm"
            onClick={addSocialLink}
          >
            + Add Social Link
          </button>
        </div>

        <div className="wp-portal__form-section">
          <h2 className="wp-portal__form-section-title">Publishing</h2>

          <div className="wp-portal__field">
            <label className="wp-portal__label" htmlFor="status">Status</label>
            <select
              id="status"
              className="wp-portal__input wp-portal__select"
              value={form.status}
              onChange={e => setField('status', e.target.value)}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          <div className="wp-portal__field">
            <label className="wp-portal__label" htmlFor="display_order">
              Display Order
            </label>
            <input
              id="display_order"
              className="wp-portal__input"
              type="number"
              value={form.display_order}
              onChange={e => setField('display_order', e.target.value)}
            />
          </div>

          <div className="wp-portal__field wp-portal__field--checkbox">
            <input
              id="is_featured"
              type="checkbox"
              className="wp-portal__checkbox"
              checked={form.is_featured}
              onChange={e => setField('is_featured', e.target.checked)}
            />
            <label
              className="wp-portal__label wp-portal__label--inline"
              htmlFor="is_featured"
            >
              Featured
            </label>
          </div>
        </div>

        <div className="wp-portal__form-section">
          <h2 className="wp-portal__form-section-title">SEO</h2>

          <div className="wp-portal__field">
            <label className="wp-portal__label" htmlFor="seo_title">SEO Title</label>
            <input
              id="seo_title"
              className="wp-portal__input"
              type="text"
              value={form.seo_title}
              onChange={e => setField('seo_title', e.target.value)}
            />
          </div>

          <div className="wp-portal__field">
            <label className="wp-portal__label" htmlFor="seo_description">
              SEO Description
            </label>
            <textarea
              id="seo_description"
              className="wp-portal__input wp-portal__textarea"
              rows={3}
              value={form.seo_description}
              onChange={e => setField('seo_description', e.target.value)}
            />
          </div>
        </div>

        <div className="wp-portal__form-actions">
          <button
            type="button"
            className="wp-portal__btn wp-portal__btn--ghost"
            onClick={() => navigate('/app/portal/admin/roster')}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="wp-portal__btn wp-portal__btn--primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : isNew ? 'Create Profile' : 'Save Changes'}
          </button>
        </div>
      </form>
    </>
  );
}
